import { useEffect, useState } from "react";
import { KeyRound, Star, UserCheck, UserX, X, Info, Users, ChevronsLeft, ChevronsRight } from "lucide-react";
import { ROLE_LABELS } from "@/lib/roles";
import { StatusBadge, EmptyState, formatDateTime } from "@/components/admin/ui";

const TABS = [
  { id: "profile", label: "Profile" },
  { id: "permissions", label: "Permissions" },
  { id: "solutions", label: "Assigned Solutions" },
  { id: "activity", label: "Activity" },
];

const ACCESS_LEVELS = [
  { value: "none", label: "None" },
  { value: "view", label: "View" },
  { value: "manage", label: "Manage" },
];

export const MemberDetailPanel = ({
  member,
  teams,
  solutions,
  assignableRoles,
  savingUserId,
  resetUserId,
  resetMessage,
  resetMessageUserId,
  activity,
  collapsed,
  onToggleCollapse,
  onChangeRole,
  onToggleBilling,
  onToggleCatalogAccess,
  onSetSolutionAccess,
  onAddTeam,
  onRemoveTeam,
  onDeactivate,
  onReactivate,
  onSendReset,
}) => {
  const [tab, setTab] = useState("profile");
  useEffect(() => { setTab("profile"); }, [member?.user_id]);

  const memberInitials = member ? (member.name || member.email || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase() : null;

  if (collapsed) {
    return (
      <div className="flex h-full min-h-0 flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm dark:border-white/10 dark:bg-[#111C33]" data-testid="org-member-detail-panel-collapsed">
        <button
          type="button"
          onClick={onToggleCollapse}
          title="Expand Details panel"
          data-testid="org-member-detail-expand-btn"
          className="rounded-lg p-2 text-slate-400 transition-colors duration-200 hover:bg-slate-50 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white"
        >
          <ChevronsLeft size={16} />
        </button>
        {member && (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#0052FF] to-[#00D4FF] text-[10px] font-extrabold text-white" title={member.name || member.email}>
            {memberInitials}
          </span>
        )}
      </div>
    );
  }

  if (!member) {
    return (
      <div className="flex h-full min-h-0 flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-[#111C33]" data-testid="org-member-detail-empty">
        <button
          type="button"
          onClick={onToggleCollapse}
          title="Collapse Details panel"
          data-testid="org-member-detail-collapse-btn"
          className="mb-2 self-end rounded-lg p-1 text-slate-400 transition-colors duration-200 hover:bg-slate-50 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white"
        >
          <ChevronsRight size={14} />
        </button>
        <div className="flex flex-1 items-center justify-center">
          <EmptyState icon={Users} title="Select a member" message="Choose a member from the list to view their profile, permissions, and activity." testId="org-member-detail-empty-state" />
        </div>
      </div>
    );
  }

  const availableTeamsToAdd = teams.filter((t) => !member.team_ids.includes(t.team_id));

  return (
    <div className="flex h-full min-h-0 flex-col rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-[#111C33]" data-testid="org-member-detail-panel">
      <div className="border-b border-slate-100 p-4 dark:border-white/10">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#0052FF] to-[#00D4FF] text-sm font-extrabold text-white">{memberInitials}</span>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-slate-950 dark:text-white" data-testid="org-member-detail-name">{member.name || member.email}</p>
              <p className="truncate text-xs text-slate-500 dark:text-slate-400" data-testid="org-member-detail-email">{member.email}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onToggleCollapse}
            title="Collapse Details panel"
            data-testid="org-member-detail-collapse-btn"
            className="shrink-0 rounded-lg p-1 text-slate-400 transition-colors duration-200 hover:bg-slate-50 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white"
          >
            <ChevronsRight size={14} />
          </button>
        </div>
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600 dark:bg-white/10 dark:text-slate-300">{ROLE_LABELS[member.role] || member.role}</span>
          {member.is_deactivated && <StatusBadge status="Deactivated" testId="org-member-detail-status" />}
          {member.incomplete_access && <StatusBadge status="Incomplete Access" testId="org-member-detail-incomplete" />}
        </div>
        {member.teams?.length > 0 && (
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400" data-testid="org-member-detail-team-name">{member.teams.map((t) => t.name).join(", ")}</p>
        )}
      </div>

      <div className="flex border-b border-slate-100 px-2 dark:border-white/10" data-testid="org-member-detail-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`org-member-detail-tab-${t.id}`}
            className={`flex-1 border-b-2 px-2 py-3 text-[11px] font-bold transition-colors duration-200 ${
              tab === t.id ? "border-[#0052FF] text-[#0052FF] dark:text-blue-400" : "border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4" data-testid="org-member-detail-content">
        {tab === "profile" && (
          <div className="space-y-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Joined</p>
              <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">{formatDateTime(member.joined_at)}</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Teams</p>
              {member.is_owner ? (
                <p className="mt-1 text-sm text-slate-400">Owners aren't assigned to teams.</p>
              ) : (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid="org-member-detail-teams">
                  {member.teams.length === 0 && <span className="text-xs text-slate-400">Not on a team yet.</span>}
                  {member.teams.map((t) => (
                    <span key={t.team_id} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 dark:bg-white/10 dark:text-slate-300">
                      {member.role === "team_lead" && <Star size={11} className="shrink-0 text-amber-500" fill="currentColor" title="Leads this team" />}
                      {t.name}
                      <button type="button" onClick={() => onRemoveTeam(member.user_id, t.team_id)} data-testid={`org-member-detail-remove-team-${t.team_id}`} className="text-slate-400 hover:text-red-500">
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                  {availableTeamsToAdd.length > 0 && (
                    <select
                      value=""
                      disabled={savingUserId === member.user_id}
                      onChange={(e) => e.target.value && onAddTeam(member.user_id, e.target.value)}
                      data-testid="org-member-detail-add-team"
                      className="rounded-lg border border-dashed border-slate-300 px-1.5 py-1 text-xs dark:border-white/15 dark:bg-white/5 dark:text-white"
                    >
                      <option value="">+ Add to team</option>
                      {availableTeamsToAdd.map((t) => (
                        <option key={t.team_id} value={t.team_id}>{t.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>
            <div className="border-t border-slate-100 pt-4 dark:border-white/10">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => onSendReset(member.user_id)}
                  disabled={resetUserId === member.user_id}
                  data-testid="org-member-detail-send-reset"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-600 transition-colors duration-200 hover:border-blue-300 hover:text-blue-600 disabled:opacity-50 dark:border-white/15 dark:text-slate-300 dark:hover:border-blue-500/40 dark:hover:text-blue-400"
                >
                  <KeyRound size={13} /> {resetUserId === member.user_id ? "Sending…" : "Send Password Reset Link"}
                </button>
                {!member.is_owner && (
                  member.is_deactivated ? (
                    <button
                      type="button"
                      onClick={() => onReactivate(member.user_id)}
                      disabled={savingUserId === member.user_id}
                      data-testid="org-member-detail-reactivate"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 px-2.5 py-1.5 text-xs font-bold text-emerald-600 disabled:opacity-50 dark:border-emerald-500/30 dark:text-emerald-400"
                    >
                      <UserCheck size={13} /> Reactivate
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onDeactivate(member.user_id)}
                      disabled={savingUserId === member.user_id}
                      data-testid="org-member-detail-deactivate"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-bold text-red-600 disabled:opacity-50 dark:border-red-500/30 dark:text-red-400"
                    >
                      <UserX size={13} /> Deactivate
                    </button>
                  )
                )}
              </div>
              {resetMessage && resetMessageUserId === member.user_id && (
                <p className="mt-2 text-xs font-semibold text-emerald-600 dark:text-emerald-400" data-testid="org-member-detail-reset-message">{resetMessage}</p>
              )}
            </div>
          </div>
        )}

        {tab === "permissions" && (
          <div className="space-y-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Role</p>
              {member.is_owner ? (
                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">{ROLE_LABELS.org_owner}</p>
              ) : (
                <select
                  value={member.role}
                  disabled={savingUserId === member.user_id || member.is_deactivated}
                  onChange={(e) => onChangeRole(member.user_id, e.target.value)}
                  data-testid="org-member-detail-role-select"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm font-semibold dark:border-white/15 dark:bg-white/5 dark:text-white"
                >
                  {Array.from(new Set([member.role, ...assignableRoles])).map((r) => (
                    <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                  ))}
                </select>
              )}
            </div>
            <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3.5 py-3 dark:border-white/10">
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Nova Billing</span>
              <input
                type="checkbox"
                checked={member.nova_billing_access}
                disabled={member.is_owner || savingUserId === member.user_id || member.is_deactivated}
                onChange={() => onToggleBilling(member.user_id, "nova_billing_access", member.nova_billing_access)}
                data-testid="org-member-detail-nova-billing-access"
              />
            </label>
            <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3.5 py-3 dark:border-white/10">
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Catalog Image Access</span>
              <input
                type="checkbox"
                checked={member.role === "team_lead" ? false : member.catalog_manage}
                disabled={member.is_owner || member.role === "org_admin" || member.role === "team_lead" || savingUserId === member.user_id || member.is_deactivated}
                title={member.role === "team_lead" ? "Team Leads cannot be granted catalog image access" : undefined}
                onChange={() => onToggleCatalogAccess(member.user_id, member.catalog_manage)}
                data-testid="org-member-detail-catalog-access"
              />
            </label>
          </div>
        )}

        {tab === "solutions" && (
          <div>
            <div className="mb-3 flex items-start gap-2 rounded-xl bg-blue-50 p-3 text-xs text-blue-700 dark:bg-blue-500/10 dark:text-blue-400" data-testid="org-member-detail-solutions-note">
              <Info size={14} className="mt-0.5 shrink-0" />
              <span>These are Nova platform modules (Front Desk, Sales, Office, Billing, People, Support) — not your Business Services or Products. "Manage" always includes "View".</span>
            </div>
            {solutions.length === 0 ? (
              <p className="text-sm text-slate-400">No solutions enabled for your organization yet.</p>
            ) : (
              <div className="space-y-2" data-testid="org-member-detail-solutions-list">
                {solutions.map((s) => {
                  const level = member.is_owner ? "manage" : (member.solution_access?.[s.solution_id] || "none");
                  const disabled = member.is_owner || savingUserId === member.user_id || member.is_deactivated;
                  return (
                    <div key={s.solution_id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3.5 py-3 dark:border-white/10">
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">{s.name}</span>
                      <div className="flex gap-1 rounded-lg bg-slate-100 p-0.5 dark:bg-white/10" data-testid={`org-member-detail-solution-${s.solution_id}`}>
                        {ACCESS_LEVELS.map((lvl) => (
                          <button
                            key={lvl.value}
                            type="button"
                            disabled={disabled}
                            onClick={() => onSetSolutionAccess(member.user_id, s.solution_id, lvl.value)}
                            data-testid={`org-member-detail-solution-${s.solution_id}-${lvl.value}`}
                            className={`rounded-md px-2.5 py-1 text-[11px] font-bold transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-60 ${
                              level === lvl.value ? "bg-[#0052FF] text-white" : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white"
                            }`}
                          >
                            {lvl.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === "activity" && (
          <div className="space-y-2" data-testid="org-member-detail-activity">
            {activity.length === 0 ? (
              <p className="text-sm text-slate-400">No activity recorded for this member yet.</p>
            ) : (
              activity.map((e) => (
                <div key={e.event_id} className="rounded-xl border border-slate-200 p-3 dark:border-white/10" data-testid={`org-member-detail-activity-${e.event_id}`}>
                  <p className="text-sm text-slate-700 dark:text-slate-300"><span className="font-semibold text-slate-950 dark:text-white">{e.actor_name}</span> {e.description}</p>
                  <p className="mt-1 text-xs text-slate-400">{formatDateTime(e.timestamp)}</p>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};
